import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes, { any } from 'prop-types';
import { useSelector } from 'react-redux';

import DrawListItem from './DrawListItem';
import Img from '../../../components/Img';
import { SortType } from '../../../components/ColumnHeader';
import DrawFilterOption from './DrawFIlterOption';
import { getAllowedFormatTypes } from '../../../utils/events';
import { useStatsig } from '../../../components/hooks/useStatsig';

import styles from './courtSchedular.scss';

const DrawList = (props) => {
    const {
        draws,
        event,
        className,
        onDrawSettingsEdit,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { checkGate, featureGates } = useStatsig();
    const isDinglesEnabled = checkGate(featureGates.DINGLES);

    const formatTypes = getAllowedFormatTypes({
        coreUI,
        isDinglesEnabled,
    });

    const {
        genderTypes,
    } = coreUI.types.tms;

    const filterableDates = useMemo(() => {
        const datesList = draws
            .filter((draw) => draw.formattedStartDate)
            .map((draw) => {
                return {
                    id: draw.formattedStartDate,
                    label: draw.formattedStartDate,
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));

        const uniquesDates = datesList.reduce((acc, date) => {
            acc.set(date.id, date);
            return acc;
        }, new Map()).values();

        return [
            ...uniquesDates,
        ];
    }, [draws]);

    const filterableOptions = [
        {
            id: 1,
            key: 'genderId',
            name: 'Gender',
            values: genderTypes,
        },
        {
            id: 2,
            key: 'formatTypeId',
            name: 'Play type',
            values: formatTypes,
        },
        ...(filterableDates?.length > 0 ? [{
            id: 3,
            key: 'formattedStartDate',
            name: 'Date',
            values: filterableDates,
        }] : []),
    ];

    const [selectedFilters, setSelectedFilters] = useState([]);
    const [sortType, setSortType] = useState(null);

    const [selectedDrawDetails, setSelectedDrawDetails] = useState(null);
    const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
    const filterContainerRef = useRef(null);

    const setSelectedDraw = (draw) => {
        setSelectedDrawDetails(draw);
    };

    const areFiltersSelected = selectedFilters.length > 0;

    const onClickFilterIcon = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (!showFiltersDropdown) {
            setShowFiltersDropdown(true);
        }
    };

    const checkAndCloseFilterDropdown = (ev) => {
        if (filterContainerRef.current && !filterContainerRef.current.contains(ev.target)) {
            ev.preventDefault();
            ev.stopPropagation();
            setShowFiltersDropdown(false);
        }
    };

    useEffect(() => {
        document.addEventListener('click', checkAndCloseFilterDropdown, true);
        return () => {
            document.removeEventListener('click', checkAndCloseFilterDropdown, true);
        };
    }, []);

    const displayableDraws = useMemo(() => {
        const filteredDraws = draws.filter((draw) => {
            if (selectedFilters.length > 0) {
                return filterableOptions
                        .map((fOption) => {
                            const fOptionSelectedFilters = selectedFilters.filter((sFilter) => sFilter.type === fOption.key);

                            if (fOptionSelectedFilters.length > 0) {
                                return fOptionSelectedFilters.reduce((acc, filter) => {
                                    return acc || (draw[filter.type] === filter.id);
                                }, false);
                            }

                            return true;
                        })
                        .every((fOptionValid) => fOptionValid);
            }

            return true;
        });

        if (sortType) {
            filteredDraws.sort((a, b) => {
                if (sortType === SortType.ASC) {
                    return a.name.localeCompare(b.name);
                }

                return b.name.localeCompare(a.name);
            });
        }

        return filteredDraws;
    }, [draws, selectedFilters, sortType]);

    const onClearFiltersClick = (filterToRemove) => () => {
        if (filterToRemove) {
            const x = selectedFilters.filter((sFilter) => {
                return sFilter.id !== filterToRemove.id;
            });
            setSelectedFilters(x);
        } else {
            setSelectedFilters([]);
        }

        setShowFiltersDropdown(false);
    };

    const onClickSortParam = (sortType) => () => {
        setSortType(sortType);
        setShowFiltersDropdown(false);
    };

    const onSetFilters = (filtersToSet) => {
        setSelectedFilters(filtersToSet);
        setShowFiltersDropdown(false);
    };

    return (
        <div className={className}>
            <div className={`${styles.header} d-flex align-items-center`}>
                <span className="mr-auto" data-testid="court-scheduler.draw-list.title">Draws</span>
                <button
                    className={`btn ${styles.filterButton} ${areFiltersSelected ? styles.selected : ''}`}
                    type="button"
                    onClick={onClickFilterIcon}
                    data-testid="court-scheduler.draw-list.filter-btn"
                >
                    <Img src="/img/v3/icons/icon-18px-filter-white.svg" />
                </button>
            </div>
            <div className={`${styles.content}`}>
                {selectedFilters.length > 0 && (
                    <div className={`${styles.contentSection} d-flex flex-wrap ${styles.appliedFiltersLayout}`}>
                        {selectedFilters.map((sFilter) => (
                            <div
                                key={`${sFilter.type}=${sFilter.id}`}
                                className={`${styles.appliedFilter} d-flex align-items-center`}
                                data-testid={`court-scheduler.draw-list.applied-filter-${sFilter.type}-${sFilter.id}`}
                            >
                                {sFilter.label} <i onClick={onClearFiltersClick(sFilter)} className={`utr utr-close ${styles.close}`} data-testid={`court-scheduler.draw-list.applied-filter-${sFilter.type}-${sFilter.id}.remove`} />
                            </div>
                        ))}
                    </div>
                )}
                {displayableDraws.map((draw) => {
                    return (
                        <DrawListItem
                            key={draw.customDrawId}
                            draw={draw}
                            selectedDraw={selectedDrawDetails}
                            setSelectedDraw={setSelectedDraw}
                            event={event}
                            className={styles.contentSection}
                            onDrawSettingEdit={onDrawSettingsEdit}
                            sportTypeId={event?.sportTypeId}
                        />
                    );
                })}
            </div>
            {showFiltersDropdown && (
                <div className={styles.drawFiltersDropdownLayout}>
                    <div className={styles.drawFiltersDropdownContent} ref={filterContainerRef}>
                        <div className={styles.section}>
                            <div
                                className={`${styles.clickable}`}
                                onClick={onClickSortParam(SortType.ASC)}
                                data-testid="court-scheduler.draw-list.sort-az"
                            >
                                Sort A - Z
                            </div>
                            <div
                                className={`${styles.clickable}`}
                                onClick={onClickSortParam(SortType.DESC)}
                                data-testid="court-scheduler.draw-list.sort-za"
                            >
                                Sort Z - A
                            </div>
                        </div>
                        <div className={styles.sectionSeparator} />
                        <div className={styles.section}>
                            <div className="d-flex align-items-center">
                                <div className={`${styles.sectionTitle} flex-1 mr4`} data-testid="court-scheduler.draw-list.filter-by-label">
                                    FILTER BY
                                </div>
                                {areFiltersSelected && (
                                    <div
                                        className={`${styles.sectionTitle} ${styles.clickable}`}
                                        onClick={onClearFiltersClick()}
                                        data-testid="court-scheduler.draw-list.clear-filters"
                                    >
                                        CLEAR
                                    </div>
                                )}
                            </div>
                            {filterableOptions.map((option) => {
                                return (
                                    <DrawFilterOption
                                        key={option.id}
                                        filterName={option.name}
                                        filterKey={option.key}
                                        filterOptions={option.values}
                                        selectedFilters={selectedFilters}
                                        setSelectedFilters={onSetFilters}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

DrawList.propTypes = {
    draws: PropTypes.arrayOf(any).isRequired,
    event: PropTypes.objectOf(any).isRequired,
    className: PropTypes.string,
    onDrawSettingsEdit: PropTypes.func.isRequired,
};

DrawList.defaultProps = {
    className: null,
};

export default DrawList;
